import useApi from '@/hooks/useApi.ts';
import {CreateUserUp, IProfileUserUp, IUser, IUserFilter, UpdateUserUp} from '@/types/user';
import {IPaginatedData, IPaginatorParams, IQueryPaginatorParams} from '@/types/table';
import {TableUtils} from '@/utils/TableUtils.ts';


interface IUseUsersApiHook {
    token?: string
    createUser: (upUser: CreateUserUp) => Promise<IUser>
    updateMe: (upUser: IProfileUserUp) => Promise<void>
    getUser: (id: string) => Promise<IUser>
    getUserByEmail: (email: string) => Promise<IUser>
    updateUser: (id: string, data: UpdateUserUp) => Promise<void>
    removeUser: (id: string) => Promise<void>
    updateCurrentTicket: (ticketId?: string, ticketNumber?: number) => Promise<void>
    removeUsers: (ids: string[]) => Promise<void>
    removeProfilePicture: (ids: string) => Promise<void>
    getUsers: (paginator: IPaginatorParams<IUser>, filter?: IUserFilter) => Promise<IPaginatedData<IUser>>
    getAllUsers: (filter?: IUserFilter) => Promise<IUser[]>
    updateLastActivity: () => Promise<IUser>
    syncUsers: () => Promise<{addedQuantity:number}>
    adminChangePassword: (id: string, password:string) => Promise<void>
}

const useUsersApi = (): IUseUsersApiHook => {
    const {token, apiGet, apiPatch, apiPost, apiPut} = useApi();


    const createUser = (upUser: CreateUserUp): Promise<IUser> => {
        return apiPost<CreateUserUp, IUser>('/users', upUser)
    }

    const getUsers = (paginator: IPaginatorParams<IUser>, filter?: IUserFilter): Promise<IPaginatedData<IUser>> => {
        const sort = TableUtils.sort2String<IUser>(paginator.sort)

        return apiGet<IPaginatedData<IUser>, IUserFilter & IQueryPaginatorParams>('/users', {
            ...paginator,
            sort, ...filter
        })
    }

    const getAllUsers = async (filter?: IUserFilter): Promise<IUser[]> => {
        const paginator: IPaginatorParams<IUser> = {
            page: 1,
            limit: 100
        }
        const result: IUser[] = []
        const paginated = await getUsers(paginator, filter)
        result.push(...paginated.items)
        const promises = await Promise.all(
            Array.from({length: paginated.totalPages}, (_, i) => i + 1)
                .slice(1)
                .map((page) => getUsers({...paginator, page}, filter))
        )

        return result.concat(...promises.map(({items}) => items).flat())
    }

    const getUser = (id: string): Promise<IUser> => {
        return apiGet<IUser>(`/users/${id}`)
    }

    const getUserByEmail = (email: string): Promise<IUser> => {
        return apiGet<IUser>(`/users/email/${email}`)
    }

    const updateUser = (id: string, upUser: UpdateUserUp): Promise<void> => {
        return apiPut<UpdateUserUp, void>(`/users/${id}`, upUser)
    }

    const updateMe = (upUser: IProfileUserUp): Promise<void> => {
        return apiPut<UpdateUserUp, void>('/users/me', upUser)
    }
    const updateLastActivity = (): Promise<IUser> => {
        return apiPut<string, IUser>('/users/activity','')
    }
    const removeUser = (id: string): Promise<void> => {
        return apiPatch(`/users/${id}`, undefined)
    }
    const removeUsers = (ids: string[]): Promise<void> => {
        // @ts-expect-error This is the way backend expects request to be sent
        return apiPatch<Pick<IUserFilter, 'ids'>, void>('/users/many', {ids})
    }

    const removeProfilePicture = (id: string): Promise<void> => {
        return apiPatch(`/users/image/${id}`, undefined)
    }

    const adminChangePassword = (id: string, password: string): Promise<void> => {
        return apiPut(`/users/pass/${id}`, {password})
    }

    const syncUsers = async (): Promise<{addedQuantity:number}> => {
        return apiPost('/users/sync', undefined)
    }

    const updateCurrentTicket = async (ticketId?: string, ticketNumber?: number): Promise<void> => {
        return apiPatch('/users/current-ticket', {ticketId, ticketNumber})
    }

    return {
        token,
        updateCurrentTicket,
        createUser,
        removeUser,
        updateUser,
        updateMe,
        removeUsers,
        getUser,
        getUserByEmail,
        getUsers,
        getAllUsers,
        removeProfilePicture,
        updateLastActivity,
        syncUsers,
        adminChangePassword
    };
};
export default useUsersApi;
